import { reactive } from 'vue';
import viewTypes from '@/common/helpers/view-types';
import { MEQYAS_PANEL_ID } from '@/common/constants';

interface UseWhiteLabelling {
  setupBranding: () => Promise<void>;
  getWhitelabelValue: (key: string) => string | undefined;
}
// State needs to be global so it's shared between use
const state = reactive({
  panelsBranding: null,
  whitelabelData: null
});

const LS_PANELS_BRANDING = `sk-panels-branding`;

/**
 * Setup global branding based on the unit panel linked to the logged in user
 * No branding will be applied for public pages or pages accessibles before a user gets a unit
 *
 * @returns {UseWhiteLabelling}
 */
export function useWhiteLabelling(): UseWhiteLabelling {
  function getWhitelabelValue(key: string): string | undefined {
    if (!state.whitelabelData) {
      return;
    }

    if (key === 'samknows_one_logo') {
      return state.whitelabelData.samknows_one_logo;
    }

    if (
      state.whitelabelData.samknows_one_site_data &&
      state.whitelabelData.samknows_one_site_data[key]
    ) {
      return state.whitelabelData.samknows_one_site_data[key];
    }

    return undefined;
  }

  async function setupBranding(): Promise<void> {
    if (!state.panelsBranding) {
      await fetchPanelsBranding();
    }
    const userUnitPanel = viewTypes.activeView?.data?.panel;
    const branding = state.panelsBranding?.find(
      (panelBranding) => panelBranding.id === userUnitPanel
    );
    state.whitelabelData = branding;

    if (userUnitPanel === MEQYAS_PANEL_ID) {
      updateFaviconsForMeqyas();
    }
  }

  async function fetchPanelsBranding(): Promise<void> {
    try {
      const response = await fetch(
        '/namespace/samknows-api/zeus/panels/branding'
      );
      const { data } = await response.json();
      state.panelsBranding = data;
      // Save to local storage for future visits in case API calls failed
      localStorage.setItem(
        LS_PANELS_BRANDING,
        JSON.stringify(state.panelsBranding)
      );
    } catch (error) {
      console.error(error);
      // Get cached branding from previous visits
      const cachedBranding = localStorage.getItem(LS_PANELS_BRANDING);
      state.panelsBranding = JSON.parse(cachedBranding);
    }
  }

  function updateFaviconsForMeqyas(): void {
    const mainFavicon: HTMLLinkElement =
      document.querySelector("link[rel~='icon']");
    if (mainFavicon && !mainFavicon.href?.includes('meqyas')) {
      mainFavicon.href = mainFavicon.href.replace(
        /\/favicon/g,
        '/favicon-meqyas'
      );
    }
    const otherFavicons: NodeListOf<HTMLLinkElement> =
      document.querySelectorAll("link[rel~='apple-touch-icon']");
    otherFavicons?.forEach((link: HTMLLinkElement) => {
      if (!link.href?.includes('meqyas')) {
        link.href = link.href.replace(/\/favicon/g, '/favicon-meqyas');
      }
    });
  }

  return {
    setupBranding,
    getWhitelabelValue
  };
}
